import { useMemo } from "react";
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { sentenceCase } from "change-case";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import { UpdateProposalLayoutContentSettingItemInput } from "generated/graphql";
import useGetOrganizationProposalLayoutContentSettings from "hooks/organization/useGetOrganizationProposalLayoutContentSettings";
import useUpdateOrganizationProposalLayoutContentSettings from "hooks/organization/useUpdateOrganizationProposalLayoutContentSettings";
import { proposalContentTypeName } from "constants/proposalSettingsDisplayType";

type FormValues = {
  items: UpdateProposalLayoutContentSettingItemInput[];
};

const disabledCheckboxSx = {
  "&.Mui-disabled": {
    pointerEvents: "auto",
    cursor: "not-allowed",
    "& .MuiSvgIcon-root": {
      backgroundColor: "#f5f5f5",
      borderRadius: "2px",
    },
  },
};

export default function LayoutContentSettingsTable() {
  const simpleLockedContentTypes = ["title_page", "statement_of_work"];
  const fullLockedContentTypes = ["title_page"];
  const brandedLockedContentTypes = ["statement_of_work"];
  const tabularLockedContentTypes = ["title_page", "statement_of_work"];
  const hiddenContentTypes = ["invoice_details"];
  const { data: rawData, loading, error } = useGetOrganizationProposalLayoutContentSettings();
  const data = rawData.filter(
    (item) => !hiddenContentTypes.includes(item.contentType.toLowerCase())
  );
  const [updateSettings, { loading: saving }] =
    useUpdateOrganizationProposalLayoutContentSettings();

  const defaultValues = useMemo(
    () => ({
      items: data.map((item) => ({
        contentType: item.contentType,
        showOnSimple: item.showOnSimple,
        showOnFull: item.showOnFull,
        showOnBranded: item.showOnBranded,
        showOnTabular: item.showOnTabular,
      })),
    }),
    [data]
  );

  const methods = useForm<FormValues>({
    defaultValues,
    values: defaultValues,
  });

  const { fields } = useFieldArray({
    control: methods.control,
    name: "items",
  });

  const onSubmit = (values: FormValues) => {
    const items = values.items.map((item) => {
      const type = item.contentType.toLowerCase();
      return {
        ...item,
        showOnSimple: simpleLockedContentTypes.includes(type) ? false : item.showOnSimple,
        showOnFull: fullLockedContentTypes.includes(type) ? false : item.showOnFull,
        showOnBranded: brandedLockedContentTypes.includes(type) ? false : item.showOnBranded,
        showOnTabular: tabularLockedContentTypes.includes(type) ? false : item.showOnTabular,
      };
    });

    updateSettings({ variables: { items } });
  };

  if (loading) {
    return (
      <Paper sx={{ p: "25px", mb: "20px" }}>
        <MDBox>
          <MDBox mb={3}>
            <MDTypography variant="h5" fontWeight="medium">
              Layout Content Settings
            </MDTypography>
          </MDBox>
          <TableSkeleton numberOfLines={5} />
        </MDBox>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: "25px", mb: "20px" }}>
        <MDBox>
          <MDBox mb={3}>
            <MDTypography variant="h5" fontWeight="medium">
              Layout Content Settings
            </MDTypography>
          </MDBox>
          <MDAlert color="error">{error.message}</MDAlert>
        </MDBox>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: "25px", mb: "20px" }}>
      <MDBox component="form" role="form" onSubmit={methods.handleSubmit(onSubmit)}>
        <MDBox mb={3}>
          <MDTypography variant="h5" fontWeight="medium">
            Layout Content Settings
          </MDTypography>
        </MDBox>

        <TableContainer>
          <Table>
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>
                  <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                    Terms
                  </MDTypography>
                </TableCell>
                <TableCell align="center">
                  <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                    Simple
                  </MDTypography>
                </TableCell>
                <TableCell align="center">
                  <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                    Full
                  </MDTypography>
                </TableCell>
                <TableCell align="center">
                  <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                    Branded
                  </MDTypography>
                </TableCell>
                <TableCell align="center">
                  <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                    Tabular
                  </MDTypography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell>
                    <MDTypography variant="body2" fontWeight="regular">
                      {proposalContentTypeName[field.contentType.toUpperCase()] ??
                        sentenceCase(field.contentType)}
                    </MDTypography>
                  </TableCell>
                  <TableCell align="center">
                    <Controller
                      name={`items.${index}.showOnSimple`}
                      control={methods.control}
                      render={({ field }) => {
                        const isLocked = simpleLockedContentTypes.includes(
                          String(fields[index]?.contentType).toLowerCase()
                        );
                        return (
                          <Checkbox
                            {...field}
                            checked={isLocked ? false : field.value}
                            disabled={isLocked}
                            sx={disabledCheckboxSx}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Controller
                      name={`items.${index}.showOnFull`}
                      control={methods.control}
                      render={({ field }) => {
                        const isLocked = fullLockedContentTypes.includes(
                          String(fields[index]?.contentType).toLowerCase()
                        );
                        return (
                          <Checkbox
                            {...field}
                            checked={isLocked ? false : field.value}
                            disabled={isLocked}
                            sx={disabledCheckboxSx}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Controller
                      name={`items.${index}.showOnBranded`}
                      control={methods.control}
                      render={({ field }) => {
                        const isLocked = brandedLockedContentTypes.includes(
                          String(fields[index]?.contentType).toLowerCase()
                        );
                        return (
                          <Checkbox
                            {...field}
                            checked={isLocked ? false : field.value}
                            disabled={isLocked}
                            sx={disabledCheckboxSx}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Controller
                      name={`items.${index}.showOnTabular`}
                      control={methods.control}
                      render={({ field }) => {
                        const isLocked = tabularLockedContentTypes.includes(
                          String(fields[index]?.contentType).toLowerCase()
                        );
                        return (
                          <Checkbox
                            {...field}
                            checked={isLocked ? false : field.value}
                            disabled={isLocked}
                            sx={disabledCheckboxSx}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        );
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <MDBox display="flex" justifyContent="flex-end" mt={2}>
          <MDButton variant="gradient" color="success" type="submit" disabled={saving}>
            Save
          </MDButton>
        </MDBox>
      </MDBox>
    </Paper>
  );
}
